import { useState } from 'react';
import { NavLink, Outlet, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  ShoppingCart,
  Package,
  Box,
  Settings,
  ArrowLeft,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { path: '/admin/users', label: 'Users', icon: Users, end: false },
  { path: '/admin/orders', label: 'Orders', icon: ShoppingCart, end: false },
  { path: '/admin/products', label: 'Products', icon: Package, end: false },
  { path: '/admin/display-cases', label: 'Display Cases', icon: Box, end: false },
  { path: '/admin/settings', label: 'Settings', icon: Settings, end: false },
];

export default function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="flex gap-0 min-h-[calc(100vh-12rem)] -mx-4 sm:-mx-6 lg:-mx-8">
      {/* Sidebar */}
      <motion.aside
        animate={{ width: collapsed ? 56 : 220 }}
        transition={{ type: 'spring', stiffness: 400, damping: 35 }}
        className="flex-shrink-0 bg-workshop-surface border-r border-white/[0.06] flex flex-col"
      >
        {/* Sidebar header */}
        <div className="p-3 border-b border-white/[0.06] flex items-center justify-between min-h-[52px]">
          <AnimatePresence mode="wait">
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.15 }}
              >
                <h2 className="text-sm font-display text-cream tracking-wide uppercase whitespace-nowrap">
                  Admin Panel
                </h2>
              </motion.div>
            )}
          </AnimatePresence>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 text-text-muted hover:text-accent hover:bg-white/[0.04] rounded-sm transition-colors flex-shrink-0"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronsRight className="w-4 h-4" />
            ) : (
              <ChevronsLeft className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 p-2 space-y-0.5">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-all duration-200 group relative
                ${isActive
                  ? 'bg-accent/15 text-accent'
                  : 'text-text-muted hover:text-cream hover:bg-white/[0.04]'
                }
                ${collapsed ? 'justify-center px-2' : ''}
                `
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.div
                      layoutId="admin-nav-active"
                      className="absolute left-0 top-1 bottom-1 w-0.5 bg-accent rounded-full"
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <AnimatePresence mode="wait">
                    {!collapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.15 }}
                        className="whitespace-nowrap overflow-hidden uppercase tracking-wide text-xs font-medium"
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Back to portal */}
        <div className="p-2 border-t border-white/[0.06]">
          <Link
            to="/"
            className={`
              flex items-center gap-2 px-3 py-2.5 rounded-sm text-xs text-accent
              hover:bg-accent/10 transition-colors uppercase tracking-wide
              ${collapsed ? 'justify-center px-2' : ''}
            `}
          >
            <ArrowLeft className="w-4 h-4 flex-shrink-0" />
            <AnimatePresence mode="wait">
              {!collapsed && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.15 }}
                  className="whitespace-nowrap overflow-hidden"
                >
                  Back to Portal
                </motion.span>
              )}
            </AnimatePresence>
          </Link>
        </div>
      </motion.aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 p-4 sm:p-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <Outlet />
        </motion.div>
      </div>
    </div>
  );
}
